import React, { useEffect, useCallback } from "react";

import { useNavigate } from "react-router-dom";

import { useToast } from "hooks/toast";

import { signInThunk } from "store/modules/auth/actions/thunks";
import { updateCustomerCertificateAction } from "store/modules/customerCertificate/actions";
import { setPermissions } from "store/modules/permission/actions";
import {
  selectCustomerProfile,
  setIsAdmin,
  setMenuListAction,
  setProfileNameAction,
} from "store/modules/profile/actions";
import {
  setTimeToCount,
  updateUser,
  changeUserPreferences,
} from "store/modules/user/actions";
import { IUser } from "store/modules/user/reducer";
import { useAppDispatch } from "store/rootReducer";

import { LeftTemplate } from "features/auth/LeftTemplate";
import { MsalAuthProvider } from "features/auth/MsalAuthProvider";
import { Form } from "features/forms/Form";

import { LanguageSelector } from "components/LanguageSelector";

import { isProduct, ProductId } from "multitenancy";

import api from "services/api";
import { IGetAllPermissionByCustomerIdAndProfileId } from "services/constants";
import { CustomerCertificateService } from "services/customerCertificate";
import { permissionServices } from "services/permissions";
import { profileServices } from "services/profile";
import { profilesService } from "services/profiles";

import { LogOperation } from "utils/entities/logOperation";

import { IProfile } from "./interface";
import { Container, LanguageSelectorWrapper } from "./styles";

export const SignIn: React.FC<{ forceMaster?: boolean }> = ({
  forceMaster = false,
}) => {
  useEffect(() => {
    (async () => {
      await LogOperation.setMachineIP();
    })();
  }, []);

  const { addToast } = useToast();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  async function handleGetProfile({
    userCustomerId,
  }: {
    userCustomerId: number;
  }) {
    const { data, success } =
      await profileServices.getUserCustomerProfileByPlace({
        customerId: userCustomerId,
      });

    if (success) {
      return data;
    }
  }

  async function recuperarTempoDeGravacaoPeloUsuario(customerId: number) {
    const response =
      await CustomerCertificateService.getCustomerCertificate(customerId);

    if (response && response.ok && response.data) {
      dispatch(updateCustomerCertificateAction(response.data));
    }

    if (response && response.data && response.data.limitRecord) {
      dispatch(setTimeToCount(response.data.limitRecord));
    }
  }

  async function handleGetPermissions({
    customerId,
    profileId,
  }: IGetAllPermissionByCustomerIdAndProfileId) {
    const { data, success } =
      await permissionServices.getAllPermissionByCustomerIdAndProfileId({
        customerId,
        profileId,
      });

    if (success) {
      dispatch(setPermissions(data || []));
      return data || [];
    }
  }

  async function GetAllMenuObjectsByUserCustomerProfile(
    customerId: any,
    UserCustomerProfileId: any,
  ) {
    const response =
      await profilesService.GetAllMenuObjectsByUserCustomerProfile(
        customerId,
        UserCustomerProfileId,
      );
    return response;
  }

  const afterSignFunction = useCallback(
    async ({ user }: { user: IUser }): Promise<string> => {
      let profile: undefined | any;

      if (user) {
        dispatch(updateUser(user));

        const profileList = await handleGetProfile({
          userCustomerId: user.id,
        });

        await recuperarTempoDeGravacaoPeloUsuario(user.customerId);

        if (profileList && profileList.length) {
          profile = profileList.find(
            (props: IProfile) => props.profile === "Master",
          );

          const selectedProfile =
            profile && profile.active
              ? {
                  ...profile,
                  profileName: profile?.profile,
                  placeId: profile?.places[0]?.id,
                  title: profile?.places[0]?.name,
                  profileId: profile?.profileId,
                }
              : {
                  ...profileList[0],
                  profileName: profileList[0]?.profile || "",
                  placeId: profileList[0]?.places[0]?.id || 0,
                  title: profileList[0]?.places[0]?.name || "N/A",
                  profileId: profileList[0]?.profileId || 0,
                };

          if (profileList[0]?.places.length === 0) {
            const placeLabel = isProduct(ProductId.Inquerito)
              ? "unidade policial"
              : "vara";

            addToast({
              timeLimitToast: 10000,
              type: "error",
              title: `Perfil sem ${placeLabel} vinculada`,
              description: `O perfil não possui uma ${placeLabel} vinculada. Entre em contato com o administrador do sistema.`,
            });
          }

          dispatch(selectCustomerProfile(selectedProfile));
          dispatch(setIsAdmin(selectedProfile.profileType === 2));
          dispatch(
            setProfileNameAction(
              profile && profile.active ? "Master" : "Administrador",
            ),
          );

          await handleGetPermissions({
            customerId: user?.customerId,
            profileId:
              profile && profile.active
                ? profile.profileId
                : profileList[0].profileId,
          });

          const profileMenu = await GetAllMenuObjectsByUserCustomerProfile(
            user?.customerId,
            profile && profile.active
              ? profile.profileId
              : profileList[0].profileId,
          );
          const menuKeyMap: Record<string, string> = {
            Hearing: "menu.audiences",
            Administration: "menu.administration",
            Report: "menu.reports",
            DashboardMaster: "menu.dashboard",
          };
          const profileMenuRenamed = profileMenu.map(
            ({ url, description, name, menu, ...rest }: any, index: any) => ({
              ...rest,
              route: url,
              label: menuKeyMap[name] || description.toUpperCase(),
              location: menu,
              show: true,
              index,
            }),
          );
          dispatch(setMenuListAction(profileMenuRenamed));
        }
      }
      return "/dashboard";
    },
    [dispatch, addToast],
  );

  const handleSsoRedirectSuccess = useCallback(
    async (result: any) => {
      if (!result?.accessToken) return;
      try {
        const { user } = await dispatch(
          signInThunk({ entraToken: result.accessToken }),
        ).unwrap();
        dispatch(changeUserPreferences(user.userCustomerPreferences));
        await afterSignFunction({ user });
        navigate("/dashboard");
      } catch {
        addToast({
          type: "error",
          title: "Erro",
          description: "Não foi possível autenticar. Tente novamente.",
        });
      }
    },
    [dispatch, navigate, addToast, afterSignFunction],
  );

  return (
    <MsalAuthProvider
      forceMaster={forceMaster}
      onRedirectSuccess={handleSsoRedirectSuccess}
    >
      <Container>
        <LanguageSelectorWrapper>
          <LanguageSelector />
        </LanguageSelectorWrapper>
        <LeftTemplate backgroundImage="default" />
        <Form
          addToast={addToast}
          variant="login"
          navigateToLogin={"/dashboard"}
          navigateToImportCertificate="/ImportCertificate"
          navigateToForgotPassword="/forgotPassword"
          navigate={navigate}
          afterSignFunction={afterSignFunction}
          api={api}
          forceMaster={forceMaster}
        />
      </Container>
    </MsalAuthProvider>
  );
};
